/**
 * Trunnion AI - Notification List Component
 * Scrollable list of notification history, grouped by date.
 */

import { useMemo } from 'react';
import { Bell } from 'lucide-react';
import { useNotificationStore } from '@/stores/notificationStore';
import { NotificationItem } from './NotificationItem';
import type { Notification } from '@/stores/notificationStore';

/**
 * Determines the date group label for a notification timestamp.
 */
function getDateGroup(timestamp: number): string {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterdayStart = todayStart - 86_400_000;

  if (timestamp >= todayStart) return 'Today';
  if (timestamp >= yesterdayStart) return 'Yesterday';
  return 'Earlier';
}

interface GroupedNotifications {
  label: string;
  items: Notification[];
}

export function NotificationList() {
  const { notificationHistory } = useNotificationStore();

  const grouped = useMemo<GroupedNotifications[]>(() => {
    // History is already sorted most-recent-first from the store
    const groups = new Map<string, Notification[]>();
    const order: string[] = [];

    for (const notification of notificationHistory) {
      const label = getDateGroup(notification.timestamp);
      if (!groups.has(label)) {
        groups.set(label, []);
        order.push(label);
      }
      groups.get(label)!.push(notification);
    }

    return order.map((label) => ({
      label,
      items: groups.get(label)!,
    }));
  }, [notificationHistory]);

  if (notificationHistory.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-trunnion-slate-800">
          <Bell className="h-6 w-6 text-trunnion-slate-500" />
        </div>
        <p className="mt-3 text-sm font-medium text-trunnion-slate-400">
          No notifications yet
        </p>
        <p className="mt-1 text-xs text-trunnion-slate-500">
          System events and alerts will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto" role="list">
      {grouped.map((group) => (
        <div key={group.label}>
          {/* Date group header */}
          <div className="sticky top-0 z-10 bg-trunnion-slate-900/95 backdrop-blur-sm border-b border-trunnion-slate-700/50 px-4 py-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-trunnion-slate-500">
              {group.label}
            </span>
          </div>

          {/* Notification items */}
          {group.items.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
